import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import WeightChart from "@/components/WeightChart";
import { CardSkeleton } from "@/components/Skeleton";
import { LineChart as LineIcon, Loader2, Trash2 } from "lucide-react";

/** The patient's own log.
 *
 *  Their number, their record: they can add one and delete one, because typos happen and
 *  nobody should have to ask the clinic to fix a digit. No targets, no goals, no praise
 *  or concern attached to any value — the chart shows what happened.
 */

function shortDate(iso) {
  if (!iso) return "";
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString(undefined, {
    weekday: "short", month: "short", day: "numeric", timeZone: "UTC",
  });
}

export default function PatientProgress() {
  const [data, setData] = useState(null);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api.get("/patient/progress")
      .then((r) => setData(r.data))
      .catch((e) => { setData({ entries: [] }); setError(formatApiError(e.response?.data?.detail)); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/patient/progress", { metric: "weight", value: parseFloat(value), unit: "lb" });
      setValue("");
      load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    setBusy(false);
  };

  const remove = async (id) => {
    setRemoving(id); setError("");
    try {
      await api.delete(`/patient/progress/${id}`);
      load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    setRemoving("");
  };

  if (!data) return <div className="space-y-4"><CardSkeleton lines={4} /></div>;

  const entries = data.entries || [];
  const latest = entries[entries.length - 1];

  return (
    <div className="space-y-6" data-testid="progress-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">My weight log</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Add a reading whenever you like. Your care team can see this too.
        </p>
      </div>

      <form onSubmit={add} className="bg-card border border-border rounded-xl p-5 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">
            Weight (lb)
          </span>
          <input data-testid="weight-input" value={value} onChange={(e) => setValue(e.target.value)}
                 inputMode="decimal" placeholder="198.4" required
                 className="mt-1.5 w-36 border border-input bg-card rounded-md px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring" />
        </label>
        <button type="submit" data-testid="weight-save" disabled={busy || !value}
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-4 py-2.5 text-sm font-semibold disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Add reading
        </button>
      </form>

      {error && (
        <p className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2"
           data-testid="progress-error">{error}</p>
      )}

      {entries.length === 0 ? (
        <EmptyState icon={LineIcon} title="Nothing logged yet"
                    description="Add your first reading and it'll start building a picture over time."
                    testid="no-progress" />
      ) : (
        <>
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-baseline justify-between gap-3">
              <p className="font-heading text-3xl font-bold tabular-nums" data-testid="weight-latest">
                {latest.value} <span className="text-base font-medium text-muted-foreground">{latest.unit}</span>
              </p>
              <span className="text-xs text-muted-foreground">Last logged {shortDate(latest.recorded_on)}</span>
            </div>
            <WeightChart entries={entries} unit={latest.unit} />
          </div>

          <div className="bg-card border border-border rounded-xl divide-y divide-border"
               data-testid="weight-entries">
            {entries.slice().reverse().map((e) => (
              <div key={e.id} data-testid={`weight-entry-${e.id}`}
                   className="px-5 py-3 flex items-center justify-between gap-3">
                <span className="text-sm">
                  <span className="font-medium tabular-nums">{e.value} {e.unit}</span>
                  <span className="text-muted-foreground"> · {shortDate(e.recorded_on)}</span>
                </span>
                <button data-testid={`weight-delete-${e.id}`} disabled={removing === e.id}
                        onClick={() => remove(e.id)} title="Remove this reading"
                        className="text-muted-foreground hover:text-destructive disabled:opacity-50">
                  {removing === e.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
